import axios from 'axios';
import { defineStore } from 'pinia';
import { platformCompact } from '../config/brand';
import { computed, ref } from 'vue';
import {
  buildHubApiUrl,
  getActiveHubEndpoint,
  getRuntimeConfig,
  isCapacitor,
  isLocal,
  isElectron,
  setActiveHub,
  getHubSessionKey,
  getHubSessionKeys,
  readTabActiveHub,
} from '../config/runtime';
import { useSocketStore } from './socket';
import { useWebRTCStore } from './webrtc';
import { saveConnection, saveConnectionCredentials } from '../services/savedConnections';
import {
  PROFILE_ID_STORAGE_KEY,
  activeProfileId,
  readPersistedProfileId,
  setActiveProfile,
} from '../services/profileScope';
import { markHubResponse } from '../utils/bootTiming';
import { isAdminRole } from '../utils/roles';
import { resolveHubOpenPlan } from '../utils/hubOpenFallback';
import {
  claimFailureMessageKey,
  classifyClaimFailure,
  readClaimedIdentity,
  shouldAttemptClaim,
  type ClaimFailureCode,
  type ClaimedIdentity,
} from '../services/hubIdentityClaim';
import {
  DEVICE_CREDENTIAL_STORAGE_KEY,
  clearDeviceCredential as clearStoredDeviceCredential,
  clearHostScopedDeviceCredentials,
  isDeviceCredentialRejected,
  readDeviceCredential,
  shouldRefreshDeviceCredential,
  storeDeviceCredential,
  type StoredDeviceCredential,
} from '../services/deviceCredential';
import {
  DEVICE_CREDENTIAL_PROBE_ENDPOINT,
  runDeviceCredentialProbe,
  type DeviceProbeVerdict,
  type DeviceRenewalOutcome,
} from '../services/deviceCredentialProbe';
import { selectBootLocalToken, selectConnectionUserToken, type LocalHubTokenSource } from '../services/localHubToken';
import { sanitizeHubRequestHeaders } from '../services/hubCorsHeaders';
// The desktop launcher home opens no hub socket (card t_f0ea3681) — the boot
// path below is one of the connects it must not make.
import { onDesktopLauncherHome } from '../services/launcherSurface';

/**
 * A hub user, as this app holds it (`/users/me`, a claim answer, a grant's
 * projection). Exported so the join flow can type the identity a link returns
 * (card `t_dc2d87c6`) without restating the shape.
 */
export interface User {
  id: string;
  email: string;
  name?: string;
  image?: string;
  role?: string;
  fullName?: string;
  username?: string;
  cloudUserId?: string | null;
  avatarImageId?: string | null;
}

/**
 * Normalize a hub user payload into the shape the UI expects.
 * The hub's `/users/me` returns the display name as `fullName` while the UI
 * reads `name` (and shows `email` as a fallback). Keep both fields in sync so
 * initials / display names work everywhere.
 */
function normalizeUser<T>(data: T): T {
  if (!data || typeof data !== 'object') return data;
  const raw = data as Record<string, unknown>;
  const name = raw.name ?? raw.fullName ?? null;
  const normalized = { ...raw } as Record<string, unknown>;
  if (name) {
    normalized.name = name;
    normalized.fullName = name;
  }
  return normalized as T;
}

// Detect if running in Electron

/**
 * The verdict on one `POST /auth/device/refresh` (F5) — the renewal's own
 * vocabulary, defined beside the probe that has to read it
 * (`services/deviceCredentialProbe.ts`):
 *  - `rotated`     the hub accepted the credential and issued a new one
 *  - `invalid`     the hub refused it (`invalid_device_token`) — it was dropped
 *  - `unavailable` no verdict (network failure, 5xx)
 *  - `none`        this session holds no device credential
 */
export type DeviceRefreshOutcome = DeviceRenewalOutcome;

/**
 * True when `apiUrl` is the CLOUD API base rather than a hub address.
 *
 * The cloud app owns its own `/api` namespace: hub paths sent there (for
 * example `/setup/status`) are answered by the cloud app — its HTML 404 when
 * it has no such route. A probe against that base is never meaningful.
 */
function isCloudApiBase(apiUrl: string): boolean {
  try {
    const config = getRuntimeConfig();
    const normalize = (u: string) => new URL(u, window.location.origin).href.replace(/\/+$/, '');
    return normalize(apiUrl) === normalize(config.cloudApiUrl);
  } catch {
    return false;
  }
}

// Get storage mechanism for persistent token storage.
// Always use localStorage so cloud tokens survive app restarts
// on all platforms (Electron, Capacitor, Web).
const getStorage = () => {
  return localStorage;
};

export const useAuthStore = defineStore('auth', () => {
  const storage = getStorage();

  // ── Per-tab hub token ────────────────────────────────────────────────────
  // localStorage is shared by every Electron tab, so the stored local-hub JWT
  // belongs to the LAST hub any tab signed in to. Validating it against THIS
  // tab's hub (the per-tab marker) fails and logs the reloaded hub tab out to
  // /home. Keep a copy per hub: each hub tab boots with its OWN session.
  function tabHubTokenKey(): string | null {
    const endpoint = readTabActiveHub();
    return endpoint ? `hubAccessToken:${endpoint.ip}:${endpoint.port}` : null;
  }

  function readTabHubToken(): string | null {
    const key = tabHubTokenKey();
    if (!key) return null;
    try {
      return storage.getItem(key);
    } catch {
      return null;
    }
  }

  /** Write the local token under the shared key AND this tab's hub key. */
  function persistLocalToken(newToken: string): void {
    storage.setItem('accessToken', newToken);
    const key = tabHubTokenKey();
    if (key) {
      try {
        storage.setItem(key, newToken);
      } catch {
        // ignore
      }
    }
    // A token this window just wrote is this window's own for the hub it is on
    // — the attribution `connectionUserToken()` reads (card `t_68d110ee`).
    localTokenSource.value = 'window';
    localTokenConfirmed.value = true;
  }

  /**
   * Where the window's local hub JWT came from — the fact that decides whether it
   * may be presented to the hub this window is pointed at (`services/localHubToken.ts`).
   *
   * `'legacy'` is the honest reading of the unscoped `accessToken` key: it holds
   * whatever hub last signed in, in ANY window (Electron tabs share localStorage,
   * which is why the per-hub key exists). Such a token answers only after the hub
   * it is pointed at has accepted it — `checkAuth()` is that acceptance at boot,
   * and `persistLocalToken()` records it.
   */
  const bootToken = selectBootLocalToken({
    windowToken: readTabHubToken(),
    legacyToken: (() => {
      try {
        return storage.getItem('accessToken');
      } catch {
        return null;
      }
    })(),
    // This window's own hub marker (set by `setActiveHub`, per window) — not the
    // shared pin: only the marker can say "this window was here".
    connectionKnown: readTabActiveHub() !== null,
  });
  const accessToken = ref<string | null>(bootToken.token);
  const localTokenSource = ref<LocalHubTokenSource>(bootToken.source);
  const localTokenConfirmed = ref(!bootToken.needsHubConfirmation);
  if (bootToken.token) {
    console.log('[AuthStore] Local hub token booted', {
      source: bootToken.source,
      needsHubConfirmation: bootToken.needsHubConfirmation,
      reason: bootToken.reason,
    });
  }
  const refreshToken = ref<string | null>(storage.getItem('refreshToken'));
  const user = ref<User | null>(null);
  const isRefreshing = ref(false);
  const refreshPromise = ref<Promise<boolean> | null>(null);
  /** True after the session-expired toast + redirect fired; reset on next successful login. */
  const sessionExpiredNotified = ref(false);

  // ── Cloud auth (OttoHome cloud account — separate from local hub auth) ──
  /**
   * The local hub JWT this window may present to the connection in use.
   *
   * Deliberately NOT `accessToken.value`: the store holds whatever token it booted
   * with, and that token can be one this connection was never issued — read from
   * the unscoped `accessToken` key (the last hub ANY window signed in to), or
   * written for the hub this window was on before it moved (an adoption, a join,
   * a manual connect — `setActiveHub()` re-pins the window and leaves the old
   * token in memory). The hub answers such a token 401, and the api layer used to
   * turn that into "Your session has expired. Please sign in again." while the
   * enrolled device credential this hub DOES accept sat unread on the same device.
   *
   * Returns null when the connection has no token of its own — the api layer then
   * presents the connection's own credential instead (`services/apiCredential.ts`,
   * precedence 4). Measured on the LAN hub: the device credential answers
   * `GET /api/devices/status-values` 200; a foreign user JWT answers 401.
   */
  function connectionUserToken(): string | null {
    const decision = selectConnectionUserToken({
      sessionToken: accessToken.value,
      windowToken: readTabHubToken(),
      source: localTokenSource.value,
      inheritedConfirmed: localTokenConfirmed.value,
      connectionKnown: readTabActiveHub() !== null,
    });
    if (accessToken.value && !decision.token) {
      console.warn('[AuthStore] Withholding the local hub token from this connection', {
        reason: decision.reason,
      });
    }
    return decision.token;
  }

  /** Is the token this window holds attributable to the connection in use? */
  function localTokenIsAttributable(): boolean {
    return connectionUserToken() !== null;
  }

  /**
   * Drop a local hub token this window cannot attribute to this connection.
   *
   * Only the in-memory copy and the unscoped key go: the token was never this
   * connection's, so no hub's own per-hub key is touched. Returns true when there
   * was something to drop — i.e. when the caller should retry with the
   * connection's own credential rather than declare the session over.
   */
  function dropUnattributableLocalToken(): boolean {
    if (!accessToken.value || localTokenIsAttributable()) return false;
    accessToken.value = null;
    user.value = null;
    localTokenSource.value = 'none';
    localTokenConfirmed.value = false;
    try {
      storage.removeItem('accessToken');
    } catch {
      // ignore
    }
    delete axios.defaults.headers.common['Authorization'];
    console.log("[AuthStore] Dropped a local hub token that was not this connection's");
    return true;
  }

  const cloudAccessToken = ref<string | null>(storage.getItem('cloudAccessToken'));
  const cloudRefreshToken = ref<string | null>(storage.getItem('cloudRefreshToken'));
  const cloudUser = ref<User | null>(null);

  /**
   * The **one** place a cloud identity is set or cleared.
   *
   * Assigning `cloudUser` directly would leave the device's *scope* behind, and
   * with it every list held per profile (`services/profileScope.ts`): the
   * launcher would paint one account's saved servers under another's name. So the
   * ref, the synchronous boot mirror, and the scope move together, always.
   *
   * Idempotent by construction: `setActiveProfile` ignores the same id twice, so
   * the re-assert on every `fetchCloudUser()` (a retry, a renewal, another
   * renderer's session) never discards a buffer already in memory.
   */
  function setCloudUser(next: User | null): void {
    cloudUser.value = next;
    const id = typeof next?.id === 'string' && next.id.trim() ? next.id.trim() : null;
    try {
      if (id) storage.setItem(PROFILE_ID_STORAGE_KEY, id);
      else storage.removeItem(PROFILE_ID_STORAGE_KEY);
    } catch {
      // A storage that refuses writes (private mode): the scope still follows this
      // session in memory — only the *next* boot paints the device bucket first.
    }
    setActiveProfile(id);
  }

  // The scope at boot, before any list is painted. An id without a cloud token is
  // a leftover (a crash between the two writes), not an identity.
  setActiveProfile(readPersistedProfileId(!!cloudAccessToken.value));

  /**
   * One-shot latch for the cloud→hub identity claim (`shouldAttemptClaim` owns
   * the decision, this owns the "once").
   *
   * `fetchHubUser()` is called from the connect path, retried 2s later by the
   * WebRTC store when no user landed, and called again on every reconnect. The
   * claim may only be spent on the FIRST of those refusals — a hub that answers
   * `identity_not_linked` would otherwise be claimed once per call, and a hub
   * that answers `no_matching_user` would be hammered forever.
   *
   * Cleared whenever the hub or the cloud session changes, because "already
   * tried" is only true for the session it was tried in.
   */
  let hubClaimAttempted = false;

  // ── Cloud session convergence across renderers ───────────────────────────
  // Every Electron tab is its own renderer with its own copy of this store, and
  // they share one localStorage. Only ONE renderer can spend an authorization
  // code (single-use), so a tab that did not handle the sign-in keeps whatever
  // `cloudAccessToken` it booted with — visibly signed out while the account's
  // token already sits in storage. Adopt what another renderer wrote instead of
  // waiting for an app restart: a `storage` event reaches every document of the
  // origin except the writer's own, and this listener writes no storage itself,
  // so there is nothing to bounce back.
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.storageArea && event.storageArea !== storage) return;

      if (event.key === 'cloudAccessToken') {
        if (event.newValue === cloudAccessToken.value) return;
        cloudAccessToken.value = event.newValue;
        cloudRefreshToken.value = event.newValue ? storage.getItem('cloudRefreshToken') : null;
        // The identity belongs to the token that just changed: it is re-read
        // from the cloud by whoever needs it (`fetchCloudUser`).
        cloudUser.value = null;
        // The scope follows the token. A sign-in in another renderer carries the
        // account id it wrote (read, never written here — that would bounce); a
        // sign-out has none, so this device falls back to its own bucket.
        setActiveProfile(event.newValue ? readPersistedProfileId(true) : null);
        hubClaimAttempted = false;
        console.log(
          event.newValue
            ? '[AuthStore] Adopted the cloud session another renderer signed in'
            : '[AuthStore] Cloud session cleared by another renderer',
        );
        return;
      }

      if (event.key === 'cloudRefreshToken' && event.newValue !== cloudRefreshToken.value) {
        cloudRefreshToken.value = event.newValue;
        return;
      }

      // Another renderer signed in as an account (or signed out): adopt the id
      // it wrote, so this renderer's lists switch with it without waiting for an
      // app restart.
      if (event.key === PROFILE_ID_STORAGE_KEY && event.newValue !== activeProfileId()) {
        setActiveProfile(event.newValue);
      }
    });
  }

  const isAuthenticated = computed(() => !!accessToken.value);
  const isCloudAuthenticated = computed(() => !!cloudAccessToken.value);
  /**
   * The administrative question every view asks — and it is a FLOOR on the F4
   * ladder (`utils/roles.ts`), not an exact match: an `owner` IS an admin, on
   * the hub (`authorize()` asks the same ladder since `dfc249c`) and therefore
   * here. Measured on the dev hub: the anchor row is `bootstrap | role owner`
   * and `GET /api/users` answers 200 for that session — while this flag said
   * "not an admin", so Settings → Users hid the surfaces that session may use.
   */
  const isAdmin = computed(() => isAdminRole(user.value?.role));

  // Backward compatibility: expose accessToken as 'token' for existing code
  const token = computed(() => accessToken.value);

  // Get API URL from runtime config
  function getApiUrl(): string {
    try {
      const config = getRuntimeConfig();
      return config.apiUrl;
    } catch {
      return '/api'; // Fallback
    }
  }

  async function runtimeFetch(url: string, options: RequestInit = {}): Promise<Response> {
    if (isElectron() && /^otto-local:\/\//i.test(url)) {
      const electron = window.electron;
      if (electron?.httpRequest) {
        const headers = (options.headers as Record<string, string>) || {};
        const rawBody =
          typeof options.body === 'string' ? options.body : options.body ? JSON.stringify(options.body) : undefined;

        const result = await electron.httpRequest({
          url,
          method: options.method || 'GET',
          headers,
          body: rawBody,
        });

        markHubResponse(url, result.status, 'ipc');

        if (result.status === 0) {
          throw new Error(result.statusText || 'Electron request failed');
        }

        return {
          ok: result.status >= 200 && result.status < 300,
          status: result.status,
          statusText: result.statusText,
          url: result.url || url,
          json: async () => result.data,
          text: async () => (typeof result.data === 'string' ? result.data : JSON.stringify(result.data)),
        } as Response;
      }
    }

    const response = await fetch(url, options);
    markHubResponse(url, response.status, 'fetch');
    return response;
  }

  /**
   * A hub/cloud JSON answer: the fields the callers below read, whatever else
   * the endpoint adds. A caller that knows its own shape names it —
   * `runtimeJsonRequest<User>(…)` for the me-endpoints, which answer the user
   * row itself.
   */
  interface RuntimeJsonBody {
    accessToken?: string;
    token?: string;
    refreshToken?: string;
    user?: User | null;
    error?: string;
    message?: string;
    needsSetup?: boolean;
    hasUsers?: boolean;
    systemName?: string;
    systemImageUrl?: string;
    cloudRegistered?: boolean;
    [key: string]: unknown;
  }

  /** A JSON body that is an object, as opposed to a string, an array or nothing. */
  function isJsonObject(value: unknown): value is RuntimeJsonBody {
    return typeof value === 'object' && value !== null;
  }

  /** The response's body as JSON, falling back to its raw text, else nothing. */
  async function readResponseBody(response: Response): Promise<unknown> {
    try {
      return await response.json();
    } catch {
      try {
        return await response.text();
      } catch {
        return null;
      }
    }
  }

  async function runtimeJsonRequest<T = RuntimeJsonBody>(url: string, options: RequestInit = {}): Promise<T> {
    const response = await runtimeFetch(url, options);
    const body = await readResponseBody(response);
    // A text body (an HTML error page, a proxy's plain text) carries none of the
    // fields below; it stays on the thrown error's `response.data` as it did.
    const answer: RuntimeJsonBody = isJsonObject(body) ? body : {};

    if (!response.ok) {
      const err = Object.assign(new Error(answer.error || answer.message || response.statusText || 'Request failed'), {
        response: { status: response.status, data: body },
      });
      throw err;
    }

    // The endpoint's own answer, in the shape the caller named (the body is the
    // API's, so only the caller can say what it is).
    return answer as T;
  }

  /**
   * Reconnect the socket with a fresh token so the hub-side user identity
   * stays current after a token refresh.
   */
  function reconnectSocketWithToken(newToken: string): void {
    try {
      const socketStore = useSocketStore();
      socketStore.disconnect();
      socketStore.connect(newToken);
    } catch (e) {
      console.warn('[AuthStore] Socket reconnect after refresh failed (safe to ignore):', e);
    }
  }

  /**
   * True when the local hub JWT is expired or within `thresholdMs` of expiry.
   */
  function isLocalTokenExpiringSoon(thresholdMs = 5 * 60 * 1000): boolean {
    const tokenValue = accessToken.value;
    if (!tokenValue) return false;
    try {
      const payload = JSON.parse(atob(tokenValue.split('.')[1]));
      const expiresAt = (payload.exp || 0) * 1000; // JWT exp is in seconds
      return Date.now() >= expiresAt - thresholdMs;
    } catch {
      return false;
    }
  }

  /**
   * Refresh the local hub access token (sliding session).
   *
   * One strategy, and it is the hub's: `POST /auth/refresh` with the current
   * token — the hub issues a fresh token as long as the old one expired within
   * the grace window.
   *
   * The silent re-login that used to follow it (a stored hub username + password
   * replayed against `POST /api/auth/login`, for hubs without the refresh route
   * or tokens outside the grace window) is gone with the rest of the
   * hub-password path (card `t_28677589`). What this window holds is either a
   * user JWT the hub itself refreshes, or the connection's **device credential**
   * (F5), which `services/apiCredential.ts` puts on every request the hub
   * accepts one on — a password somebody once typed is neither, and on this
   * fleet it is usually dead anyway (`/auth/login` is gated to
   * `authKind === 'legacy'` rows).
   *
   * On success the socket reconnects with the new token. Returns false when the
   * session cannot be recovered without user interaction.
   */
  async function refreshLocalHubToken(): Promise<boolean> {
    // Prevent multiple concurrent refresh attempts
    if (isRefreshing.value && refreshPromise.value) {
      return refreshPromise.value;
    }

    if (!accessToken.value) {
      console.warn('[AuthStore] No local hub token to refresh');
      return false;
    }

    isRefreshing.value = true;
    refreshPromise.value = (async () => {
      try {
        const apiUrl = getApiUrl();
        // Only headers the hub's CORS allow-list names may go out — anything
        // else is refused by the browser at preflight and the request never
        // leaves this window (card `t_68d110ee`: a dead `X-Skip-Interceptor`
        // here is what killed this refresh, and with it the silent re-login
        // behind it).
        const { headers, refused } = sanitizeHubRequestHeaders({ 'Content-Type': 'application/json' });
        if (refused.length > 0) {
          console.warn('[AuthStore] Dropped headers the hub does not allow on /auth/refresh', { refused });
        }
        const data = await runtimeJsonRequest(`${apiUrl}/auth/refresh`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ token: accessToken.value }),
        });
        const newToken = data.accessToken || data.token;
        if (newToken) {
          accessToken.value = newToken;
          persistLocalToken(newToken);
          if (data.user) user.value = normalizeUser(data.user);
          axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          reconnectSocketWithToken(newToken);
          console.log('[AuthStore] Local hub token refreshed (sliding session)');
          return true;
        }
        // A hub that answered without a token (older builds, a route that
        // changed shape): the session cannot be recovered from here. The
        // device credential still opens the routes it is accepted on, and the
        // caller's own 401 path decides what the person sees.
        console.warn('[AuthStore] /auth/refresh returned no token');
        return false;
      } catch (__caught: unknown) {
        const error = __caught as Error;
        // Refused (401, outside the grace window), unreachable, or a hub
        // without the route (404) — nothing silent is left to try.
        console.warn('[AuthStore] /auth/refresh failed:', error?.message);
        return false;
      } finally {
        isRefreshing.value = false;
        refreshPromise.value = null;
      }
    })();

    return refreshPromise.value;
  }

  /**
   * Backward-compatible alias for callers that used the old refresh function.
   */
  async function refreshAccessToken(): Promise<boolean> {
    return refreshLocalHubToken();
  }

  /**
   * Session cannot be recovered silently: show the session-expired toast and
   * send the user to the connect screen (Electron/Capacitor) or login page
   * (browser). Guarded so parallel 401 failures notify only once.
   */
  async function notifySessionExpired(): Promise<void> {
    if (sessionExpiredNotified.value) return;
    sessionExpiredNotified.value = true;

    // Inside a hub tab, keep the tab where it is: the session can be brought
    // back with the saved credentials via the "connection lost" overlay
    // (Reconnect / Close). Closing the tab and falling back to the launcher
    // made a dropped session look like the tab had navigated home.
    if (isElectron()) {
      try {
        const router = (await import('../router')).default;
        const current = router.currentRoute.value;
        const inHubTab = !current.meta.bare && !['/home', '/servers', '/login', '/setup'].includes(current.path);
        if (inHubTab) {
          const { useConnectionLostStore } = await import('./connectionLost');
          useConnectionLostStore().markLost('session');
          // No logout(): the overlay owns the dead session until the user
          // reconnects (fresh silent login) or closes the tab.
          return;
        }
      } catch (e) {
        console.warn('[AuthStore] Failed to raise the connection-lost overlay:', e);
      }
    }

    try {
      const { useToastStore } = await import('./toast');
      useToastStore().addToast({
        type: 'warning',
        message: 'Your session has expired. Please sign in again.',
        duration: 6000,
      });
    } catch (e) {
      console.warn('[AuthStore] Failed to show session-expired toast:', e);
    }

    logout();

    try {
      if (isElectron()) {
        const { leaveHubToLauncher } = await import('../services/appNavigation');
        await leaveHubToLauncher();
      } else {
        const router = (await import('../router')).default;
        await router.push(isCapacitor() ? '/home' : '/login');
      }
    } catch (e) {
      console.warn('[AuthStore] Session-expired redirect failed:', e);
    }
  }

  async function login(email: string, password: string) {
    try {
      const apiUrl = getApiUrl();
      const data = await runtimeJsonRequest(`${apiUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });
      // Support both cloud (accessToken/refreshToken) and local (token) responses
      const newAccessToken = data.accessToken || data.token || null;
      const newRefreshToken = data.refreshToken || null;
      const newUser = data.user ?? null;

      accessToken.value = newAccessToken;
      refreshToken.value = newRefreshToken;
      user.value = newUser;

      // Store tokens in appropriate storage. A login answered by a HUB's own API
      // is this window's session for that hub, so it is recorded as such (the
      // window's own key + its attribution); a cloud answer is not a hub JWT and
      // keeps the historical storage only (card `t_68d110ee`).
      if (newAccessToken) {
        if (isCloudApiBase(apiUrl)) storage.setItem('accessToken', newAccessToken);
        else persistLocalToken(newAccessToken);
      }
      if (newRefreshToken) storage.setItem('refreshToken', newRefreshToken);

      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;

      // Connect socket with token
      const socketStore = useSocketStore();
      socketStore.connect(newAccessToken ?? undefined);
      console.log('[AuthStore] Socket connection initiated after login');

      return true;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  }

  // OAuth login (cloud only)
  async function loginWithOAuth(provider: 'google' | 'github') {
    try {
      const config = getRuntimeConfig();
      if (config.mode !== 'cloud') {
        return { success: false, error: 'OAuth is only available in cloud mode' };
      }

      const cloudApiUrl = config.cloudApiUrl;
      window.location.href = `${cloudApiUrl}/api/auth/${provider}`;
      return { success: true };
    } catch (__caught: unknown) {
      const error = __caught as Error;
      console.error('OAuth login failed:', error);
      return { success: false, error: error.message || 'OAuth login failed' };
    }
  }

  /**
   * Handle OAuth callback (parse tokens from URL)
   */
  async function handleOAuthCallback(accessTokenParam: string, refreshTokenParam: string) {
    try {
      accessToken.value = accessTokenParam;
      refreshToken.value = refreshTokenParam;

      storage.setItem('accessToken', accessTokenParam);
      storage.setItem('refreshToken', refreshTokenParam);

      axios.defaults.headers.common['Authorization'] = `Bearer ${accessTokenParam}`;

      // Fetch user info
      const apiUrl = getApiUrl();
      const meEndpoint = isLocal() ? '/users/me' : '/auth/me';
      const data = await runtimeJsonRequest<User>(`${apiUrl}${meEndpoint}`, {
        headers: {
          Authorization: `Bearer ${accessTokenParam}`,
        },
      });
      user.value = data;

      // Connect socket
      const socketStore = useSocketStore();
      socketStore.connect(accessTokenParam);
      console.log('[AuthStore] OAuth callback processed');

      return true;
    } catch (error) {
      console.error('OAuth callback processing failed:', error);
      logout();
      return false;
    }
  }

  /**
   * Hub/local sign-out.
   *
   * `closeTabs` is the **explicit** half of a sign-out: a caller sets it when the
   * person performed the logout (`UserDrawer`, the launcher's Disconnect, the
   * account page) — never the silent paths (`checkAuth`, the stale-socket guard, a
   * reconnect, `notifySessionExpired`), which keep their tab and its neighbours and
   * show the connection-lost overlay instead. It closes every tab but the pinned
   * launcher (`services/appNavigation.ts` → `closeAllTabsToLauncher`).
   */
  function logout(options: { closeTabs?: boolean } = {}): void {
    accessToken.value = null;
    refreshToken.value = null;
    user.value = null;
    // No token, no attribution question.
    localTokenSource.value = 'none';
    localTokenConfirmed.value = false;

    // A fresh session makes its own claim decision (PLAN.md F13).
    hubClaimAttempted = false;

    storage.removeItem('accessToken');
    storage.removeItem('refreshToken');
    // Drop this tab's hub-scoped copy too — otherwise a later reload of this
    // tab would silently resurrect the logged-out session.
    const scopedKey = tabHubTokenKey();
    if (scopedKey) {
      try {
        storage.removeItem(scopedKey);
      } catch {
        // ignore
      }
    }
    delete axios.defaults.headers.common['Authorization'];

    // Disconnect socket (safe — won't crash if socket is stale or in WebRTC bridge mode)
    try {
      const socketStore = useSocketStore();
      socketStore.disconnect();
    } catch (e) {
      console.warn('[AuthStore] Socket disconnect on logout failed (safe to ignore):', e);
    }

    // Clear per-hub last path so next login starts from home
    try {
      for (const key of getHubSessionKeys()) {
        localStorage.removeItem(`lastHubPath:${key}`);
      }
    } catch {
      // ignore
    }

    // Drop per-hub cached data (device automations keyed by deviceId can
    // collide across hubs) so a later login to another hub starts clean.
    import('./deviceAutomations')
      .then(({ useDeviceAutomationsStore }) => {
        useDeviceAutomationsStore().resetForHubSwitch();
      })
      .catch(() => {
        // ignore
      });

    // Last: the person signed out, so every tab they had open goes with the
    // session (the pinned launcher survives and is what is left).
    if (options.closeTabs) void closeTabsOnLogout();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Cloud auth (OttoHome cloud account)
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Make an HTTP request — uses Electron IPC proxy when available to bypass CORS
   */
  async function cloudFetch(url: string, options: RequestInit = {}): Promise<Response> {
    if (isElectron()) {
      const electron = window.electron;
      if (electron?.httpRequest) {
        console.log('[AuthStore] cloudFetch via electron', options.method || 'GET', url);
        const result = await electron.httpRequest({
          url,
          method: options.method || 'GET',
          headers: (options.headers as Record<string, string>) || {},
          body: typeof options.body === 'string' ? options.body : JSON.stringify(options.body),
        });
        console.log('[AuthStore] cloudFetch result', result.status, result.statusText, url);
        if (result.status === 0) {
          throw new Error(result.statusText);
        }
        return {
          ok: result.status >= 200 && result.status < 300,
          status: result.status,
          statusText: result.statusText,
          json: async () => result.data,
          text: async () => (typeof result.data === 'string' ? result.data : JSON.stringify(result.data)),
        } as Response;
      }
    }
    return fetch(url, options);
  }

  /**
   * Login to cloud via OAuth PKCE flow.
   * This opens the system browser for user authorization (no password in app).
   * After the callback, it exchanges the code for tokens and stores them locally.
   *
   * This is the ONLY cloud sign-in: the cloud retired password sign-in
   * (otto-web commit 0882fc0 answers `code: "password_grant_retired"`), so there
   * is deliberately no password-shaped sibling of this function.
   */
  async function loginToCloudViaOAuth(callbackUrl: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { handleCloudOAuthCallback } = await import('../services/cloud-oauth');
      const result = await handleCloudOAuthCallback(callbackUrl);

      if (!result.success || !result.accessToken) {
        return { success: false, error: result.error || 'OAuth callback failed' };
      }

      cloudAccessToken.value = result.accessToken;
      cloudRefreshToken.value = result.refreshToken || null;

      storage.setItem('cloudAccessToken', result.accessToken);
      if (result.refreshToken) {
        storage.setItem('cloudRefreshToken', result.refreshToken);
      }

      await fetchCloudUser();
      return { success: true };
    } catch (__caught: unknown) {
      const error = __caught as Error;
      console.error('[AuthStore] loginToCloudViaOAuth failed:', error.message);
      return { success: false, error: error.message || 'Cloud OAuth login failed' };
    }
  }

  /**
   * The signed-in account's own record, from the cloud (`/oauth2/userinfo`).
   *
   * Two failures, two answers (card `t_f4f925f9`): an answer that **refuses**
   * the token ends (or renews) the session, and a cloud that **cannot be
   * reached** leaves it exactly as it was — offline is not a verdict, and the
   * buffered server list is what an offline launch paints from.
   */
  async function fetchCloudUser(): Promise<void> {
    if (!cloudAccessToken.value) return;
    try {
      const config = getRuntimeConfig();
      const base = config.cloudApiUrl || config.apiUrl || '';
      const userUrl = base ? `${base}/oauth2/userinfo` : '/api/oauth2/userinfo';
      console.log('[AuthStore] fetchCloudUser', userUrl);
      const response = await cloudFetch(userUrl, {
        headers: { Authorization: `Bearer ${cloudAccessToken.value}` },
      });
      if (!response.ok) {
        // The cloud ANSWERED and refused this token: the session may be over —
        // renew it, or end it (`renewCloudSessionOrClear`).
        await renewCloudSessionOrClear(response.status);
        return;
      }
      try {
        setCloudUser((await response.json()) as User | null);
      } catch (error) {
        // A 2xx whose body is not JSON (a captive portal, an HTML error page):
        // the request arrived, so this is not a verdict on the session either.
        console.warn('[AuthStore] fetchCloudUser could not read the answer (session kept):', error);
        return;
      }
      console.log('[AuthStore] fetchCloudUser success');
    } catch (error) {
      // The cloud did not answer AT ALL (offline, DNS, a blocked origin, the
      // tunnel down). That is a fact about this device's network, not a verdict
      // on the session: clearing the token here would sign the device out for
      // being offline — and an offline launch is exactly the case the buffered
      // server list exists for (plan `LOCAL-HTTP-AND-CLOUD-ADOPTION.md` §2/C2,
      // card `t_f4f925f9`). Keep the session; the next launch tries again.
      console.warn('[AuthStore] fetchCloudUser could not reach the cloud (session kept):', error);
    }
  }

  /**
   * The cloud refused the access token (a non-2xx answer): renew it, or — when
   * there is nothing left to renew with — end the session. This is the one place
   * that decision is made, so "the cloud is unreachable" and "the session is
   * over" cannot blur into each other again.
   */
  async function renewCloudSessionOrClear(status: number): Promise<void> {
    console.warn(`[AuthStore] fetchCloudUser refused (${status}), attempting token refresh...`);
    const refreshed = await refreshCloudAccessToken();
    if (refreshed) return;
    cloudAccessToken.value = null;
    cloudRefreshToken.value = null;
    storage.removeItem('cloudAccessToken');
    storage.removeItem('cloudRefreshToken');
    console.warn('[AuthStore] Cloud token refresh failed, cleared cloud tokens');
  }

  /**
   * In-flight cloud refresh, shared by every caller in this renderer.
   *
   * Not reactive on purpose: it is an identity token for "a refresh is already
   * running", never rendered. Same shape as `refreshPromise` above, which
   * `refreshLocalHubToken` already relies on.
   */
  let cloudRefreshPromise: Promise<boolean> | null = null;

  /**
   * Refresh cloud access token using refresh token.
   * Returns true if the token was refreshed successfully.
   *
   * Cloud refresh tokens ROTATE, so two overlapping refreshes are not merely
   * wasteful — the loser presents a refresh token the server has already
   * invalidated, and a refresh failure here clears the session
   * (`renewCloudSessionOrClear`). Four concurrent `POST /oauth2/token` were
   * observed in one 300 ms window, so this collapses concurrent callers onto one
   * request and re-reads shared storage before spending the refresh token.
   */
  async function refreshCloudAccessToken(): Promise<boolean> {
    if (!cloudRefreshToken.value) {
      console.warn('[AuthStore] No cloud refresh token available');
      return false;
    }

    if (cloudRefreshPromise) return cloudRefreshPromise;
    cloudRefreshPromise = performCloudTokenRefresh().finally(() => {
      cloudRefreshPromise = null;
    });
    return cloudRefreshPromise;
  }

  /** The single in-flight refresh body. Only `refreshCloudAccessToken` calls this. */
  async function performCloudTokenRefresh(): Promise<boolean> {
    try {
      // Another renderer may have rotated the pair while we were queued (tabs
      // share localStorage). Adopt theirs instead of spending a spent token.
      const sharedRefreshToken = storage.getItem('cloudRefreshToken');
      if (sharedRefreshToken && sharedRefreshToken !== cloudRefreshToken.value) {
        cloudRefreshToken.value = sharedRefreshToken;
        const sharedAccessToken = storage.getItem('cloudAccessToken');
        if (sharedAccessToken) cloudAccessToken.value = sharedAccessToken;
        console.log('[AuthStore] Adopted a cloud token another renderer refreshed');
        return true;
      }

      const config = getRuntimeConfig();
      const base = config.cloudApiUrl || config.apiUrl || '';
      const tokenUrl = base ? `${base}/oauth2/token` : '/api/oauth2/token';

      const response = await cloudFetch(tokenUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ grant_type: 'refresh_token', refresh_token: cloudRefreshToken.value }),
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        console.error('[AuthStore] Cloud token refresh failed:', errBody?.error || response.statusText);
        return false;
      }

      const data = await response.json();
      const newAccessToken = data.access_token || data.accessToken;
      const newRefreshToken = data.refresh_token || data.refreshToken || cloudRefreshToken.value;

      cloudAccessToken.value = newAccessToken;
      cloudRefreshToken.value = newRefreshToken;

      storage.setItem('cloudAccessToken', newAccessToken);
      storage.setItem('cloudRefreshToken', newRefreshToken);

      console.log('[AuthStore] Cloud access token refreshed successfully');
      return true;
    } catch (error) {
      console.error('[AuthStore] Cloud token refresh error:', error);
      return false;
    }
  }

  /**
   * Ensure cloud token is valid (refresh if within 5 min of expiry).
   * Call this before any cloud API call.
   */
  async function ensureCloudToken(): Promise<boolean> {
    if (!cloudAccessToken.value) return false;
    try {
      // Decode JWT to check expiry
      const payload = JSON.parse(atob(cloudAccessToken.value.split('.')[1]));
      const expiresAt = payload.exp * 1000; // JWT exp is in seconds
      const fiveMinutes = 5 * 60 * 1000;
      if (Date.now() > expiresAt - fiveMinutes) {
        console.log('[AuthStore] Cloud token near expiry, refreshing...');
        return await refreshCloudAccessToken();
      }
      return true;
    } catch {
      // Can't decode — try to refresh anyway
      return await refreshCloudAccessToken();
    }
  }

  // Guards the "cloud session expired" toast so parallel failures notify once.
  let cloudSessionExpiredNotified = false;

  /**
   * The tab teardown of an explicit sign-out, imported on demand so this store's
   * import graph is unchanged for the many callers that never use it (the same
   * shape the session-expiry path below already uses).
   */
  async function closeTabsOnLogout(): Promise<void> {
    try {
      const { closeAllTabsToLauncher } = await import('../services/appNavigation');
      await closeAllTabsToLauncher();
    } catch (error) {
      console.warn('[AuthStore] could not close the open tabs on sign-out:', error);
    }
  }

  /**
   * The signed-out device keeps **no** server list (owner, 2026-10-04): the device
   * bucket is where the pre-profile keys live, so a signed-out screen must not
   * paint the previous account's servers. Imported on demand, like the tab
   * teardown above, and fired last so the lists go once the account is gone.
   */
  function clearSignedOutListsOnLogout(): void {
    void import('../services/signedOutLists')
      .then(({ clearSignedOutLists }) => clearSignedOutLists())
      .catch((error: unknown) => {
        console.warn('[AuthStore] could not clear the signed-out server lists:', error);
      });
  }

  /**
   * Cloud account sign-out.
   *
   * `closeTabs` is the explicit half, exactly as in `logout()`: the cloud panel's
   * "Sign out of cloud account" and the server menu's "Sign Out" set it — the
   * silent `invalidateCloudSession` (two failed refreshes) deliberately does not.
   */
  function logoutFromCloud(options: { closeTabs?: boolean } = {}): void {
    cloudAccessToken.value = null;
    cloudRefreshToken.value = null;
    // Clears the scope too: the device falls back to its own saved list.
    setCloudUser(null);
    storage.removeItem('cloudAccessToken');
    storage.removeItem('cloudRefreshToken');
    // No cloud identity left to claim with; the next sign-in decides again.
    hubClaimAttempted = false;

    // Clear per-hub last path so next login starts from home
    try {
      const webrtcStore = useWebRTCStore();
      for (const key of getHubSessionKeys(webrtcStore.hubId)) {
        localStorage.removeItem(`lastHubPath:${key}`);
      }
    } catch {
      // ignore
    }

    if (options.closeTabs) void closeTabsOnLogout();
    // The account is gone: the device falls back to **no** list, not to the last
    // one it happened to write (`services/signedOutLists.ts`).
    clearSignedOutListsOnLogout();
  }

  /**
   * Cloud session cannot be restored (refresh token missing/invalid/expired or
   * the refresh endpoint failed repeatedly). Clear the stale cloud auth and
   * notify the user once, so the UI falls back to the "Sign in" state instead
   * of silently breaking cloud features (cloud hub list, snapshots, WebRTC).
   */
  function invalidateCloudSession(message?: string): void {
    const hadToken = !!cloudAccessToken.value;
    cloudAccessToken.value = null;
    cloudRefreshToken.value = null;
    // Clears the scope too: the device falls back to its own saved list.
    setCloudUser(null);
    storage.removeItem('cloudAccessToken');
    storage.removeItem('cloudRefreshToken');

    // The session that was claimed (or refused) is gone.
    hubClaimAttempted = false;

    // Clear per-hub last path so next login starts from home
    try {
      const webrtcStore = useWebRTCStore();
      for (const key of getHubSessionKeys(webrtcStore.hubId)) {
        localStorage.removeItem(`lastHubPath:${key}`);
      }
    } catch {
      // ignore
    }

    // The account is gone here too: the device shows no list until someone signs
    // in (`services/signedOutLists.ts`).
    clearSignedOutListsOnLogout();

    if (hadToken && !cloudSessionExpiredNotified) {
      cloudSessionExpiredNotified = true;
      const msg = message || 'Your cloud session has expired. Please sign in again.';
      void import('./toast')
        .then(({ useToastStore }) => {
          useToastStore().addToast({ type: 'warning', message: msg, duration: 6000 });
        })
        .catch(() => {
          console.warn('[AuthStore] Failed to show cloud session-expired toast');
        });
      // Allow a future expiry to notify again after a minute.
      window.setTimeout(() => {
        cloudSessionExpiredNotified = false;
      }, 60_000);
    }
  }

  /**
   * Initialize cloud auth from otto-web's NextAuth session.
   * When otto-ui is embedded in otto-web (at /app), the browser already
   * has a NextAuth session cookie. We exchange it for a Bearer token
   * via the session-token bridge API.
   */
  async function initFromWebSession(): Promise<boolean> {
    try {
      // Call the session-token bridge endpoint to get tokens
      const response = await fetch('/api/auth/session-token', {
        credentials: 'include', // Send NextAuth session cookie
      });

      if (!response.ok) {
        // Not authenticated with otto-web — user needs to sign in
        return false;
      }

      const data = await response.json();

      cloudAccessToken.value = data.accessToken;
      cloudRefreshToken.value = data.refreshToken;
      setCloudUser((data.user as User | null) ?? null);

      storage.setItem('cloudAccessToken', data.accessToken);
      if (data.refreshToken) storage.setItem('cloudRefreshToken', data.refreshToken);

      console.log('[AuthStore] Web session initialized for user:', data.user?.email);
      return true;
    } catch (err) {
      console.error('[AuthStore] Failed to init from web session:', err);
      return false;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Hub login (connects to a specific local hub by IP / port)
  // ─────────────────────────────────────────────────────────────────────────

  async function loginToHub(
    ip: string,
    port: number,
    email: string,
    password: string,
    rememberHub = true,
    opts: { silentReauth?: boolean; secure?: boolean } = {},
  ): Promise<{ success: boolean; error?: string; needsSetup?: boolean }> {
    try {
      // The address the caller measured, with its scheme (`t_cf6ea0e5`): a hub
      // that answered over TLS must be dialled over TLS, and one that answered
      // cleartext (this fleet's hubs do, on 16161) must stay cleartext. No
      // scheme information keeps the historical `http://`.
      const hubApi = buildHubApiUrl(ip, port, opts.secure);

      // Fresh hub (no users yet)? Route to the first-user setup wizard
      // instead of failing the login attempt. Skipped for silent re-auth
      // (same hub is already known to have users).
      if (!opts.silentReauth) {
        try {
          const setupRes = await runtimeFetch(`${hubApi}/setup/status`);
          const setupData = await setupRes.json();
          if (setupData?.needsSetup) {
            // Fresh hub (no local users yet): the first-user setup wizard will
            // run against THIS hub. Do not keep the previously connected hub's
            // session alive meanwhile — the app would otherwise render the old
            // hub's shell (navigation, branding, cached data) around the new
            // hub's setup wizard, and hub-scoped API calls would mix sessions.
            logout();
            const { useDashboardStore } = await import('./dashboard');
            const { useEntitiesStore } = await import('./entities');
            useDashboardStore().resetForHubSwitch();
            useEntitiesStore().resetForHubSwitch();
            const { useDeviceAutomationsStore } = await import('./deviceAutomations');
            useDeviceAutomationsStore().resetForHubSwitch();
            const { useCamerasStore } = await import('./cameras');
            useCamerasStore().resetForHubSwitch();
            const { useCameraSnapshotsStore } = await import('./cameraSnapshots');
            useCameraSnapshotsStore().resetForHubSwitch();

            const parsed = new URL(hubApi);
            let hubPort = Number(parsed.port || (parsed.protocol === 'https:' ? '443' : '80'));
            let hubSecure = parsed.protocol === 'https:';
            // The caller's port may be a redirect-only port (e.g. :80 → :443):
            // requests get bounced and POSTs fail. Prefer the port the hub
            // itself advertises via the public /info endpoint, like discovery —
            // but only when this client could actually use it. `/api/info`
            // advertises the hub's OWN web port, which on this fleet is **443,
            // TLS only, behind a self-signed certificate** — the wizard would
            // then be pointed at an endpoint this device cannot reach, while the
            // cleartext endpoint it just answered on (16161) works (measured,
            // card `t_cf6ea0e5`).
            try {
              const infoRes = await runtimeFetch(`${hubApi}/info`);
              const info = await infoRes.json();
              const advertisedPort = Number(info?.port);
              const advertiseUsable = advertisedPort > 0 && !(advertisedPort === 443 && parsed.protocol !== 'https:');
              if (advertiseUsable) {
                hubPort = advertisedPort;
                if (hubPort === 443) hubSecure = true;
              }
            } catch {
              // /info unreachable — keep the caller's endpoint
            }
            setActiveHub(parsed.hostname, hubPort, hubSecure);
            // Tear down any stale remote/cloud WebRTC session before switching.
            const { useWebRTCStore } = await import('./webrtc');
            const webrtcStore = useWebRTCStore();
            if (webrtcStore.connected || webrtcStore.connecting) {
              webrtcStore.disconnect();
            }
            // The cached public setup status still belongs to the previous hub —
            // refresh it against the hub we just switched to so the router guard
            // and login page agree that this hub needs setup.
            await fetchSetupStatus(true);
            return { success: false, needsSetup: true };
          }
        } catch {
          // setup/status unreachable — fall through to the normal login attempt.
        }
      }

      const response = await runtimeFetch(`${hubApi}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      const newToken = data.accessToken || data.token;
      const newRefresh = data.refreshToken || null;
      const newUser = data.user;

      let resolvedIp = ip;
      let resolvedPort = port;
      // Three-valued while it is being resolved (`t_cf6ea0e5`): the caller's
      // measured scheme first, then what the answer actually came from — the
      // response URL is the strongest evidence there is. `undefined` (an opaque
      // response, no hint) keeps `setActiveHub`'s own rule below.
      let secure = opts.secure;

      try {
        const resolvedUrl = response.url;
        if (resolvedUrl) {
          const parsed = new URL(resolvedUrl);
          resolvedIp = parsed.hostname || ip;
          resolvedPort = Number(parsed.port || (parsed.protocol === 'https:' ? '443' : '80')) || port;
          secure = parsed.protocol === 'https:';
        }
      } catch {
        const responseUrl = response.url;
        if (responseUrl) secure = responseUrl.startsWith('https://');
      }

      // What the session is actually pointed at, as a boolean: nothing said
      // falls back to the port rule `setActiveHub` itself applies (443 IS TLS).
      const activeSecure = typeof secure === 'boolean' ? secure : resolvedPort === 443;

      const { useDashboardStore } = await import('./dashboard');
      const { useEntitiesStore } = await import('./entities');
      const { useWebRTCStore } = await import('./webrtc');

      // Switching to a local hub must always tear down any stale remote cloud
      // WebRTC session first. If WebRTC remains connected, local Electron API
      // calls can be misrouted through the remote transport layer.
      const webrtcStore = useWebRTCStore();
      if (webrtcStore.connected || webrtcStore.connecting) {
        webrtcStore.disconnect();
      }

      // Hub-switch store resets are only needed when switching hubs; for a
      // silent re-auth of the SAME hub, skip them to avoid clearing live UI
      // state (entities, dashboards, automations) mid-session.
      if (!opts.silentReauth) {
        useDashboardStore().resetForHubSwitch();
        useEntitiesStore().resetForHubSwitch();
        // Device automation caches are keyed by deviceId (which can collide across
        // hubs after a backup restore) — clear them so the new hub re-fetches.
        const { useDeviceAutomationsStore } = await import('./deviceAutomations');
        useDeviceAutomationsStore().resetForHubSwitch();
        // Cameras + snapshots are hub-scoped too — drop them so the newly
        // connected hub's cameras load (otherwise the cameras page shows the
        // previous hub's list or a stale empty page).
        const { useCamerasStore } = await import('./cameras');
        useCamerasStore().resetForHubSwitch();
        const { useCameraSnapshotsStore } = await import('./cameraSnapshots');
        useCameraSnapshotsStore().resetForHubSwitch();
      }

      // Point runtime config at this hub
      setActiveHub(resolvedIp, resolvedPort, activeSecure);

      accessToken.value = newToken;
      refreshToken.value = newRefresh;
      user.value = newUser;

      persistLocalToken(newToken);
      if (newRefresh) storage.setItem('refreshToken', newRefresh);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

      // Connect socket — always drop the previous transport first. A socket left
      // over from another hub (or a cloud session's WebRTC status bridge) would
      // make socketStore.connect() reuse the old target and device status
      // updates would never arrive on the new hub (commands still go over HTTP).
      const socketStore = useSocketStore();
      socketStore.disconnect();
      socketStore.connect(newToken);

      // Load location branding (name + image) so the app icon updates right away.
      try {
        const { useSystemStore } = await import('./system');
        await useSystemStore().load();
      } catch {
        // Non-critical — branding reloads on layout mount anyway
      }

      // Save connection for quick reconnect (with credentials if rememberHub)
      if (rememberHub) {
        const connId = `${resolvedIp}:${resolvedPort}`;
        // Capture hub identity (location name + branding) from the public
        // /api/info endpoint so the saved card shows the right name + image.
        let hubName = newUser?.name || `${platformCompact} (${resolvedIp})`;
        let hubImage: string | undefined;
        let hubIcon: string | undefined;
        let hubId: string | undefined;
        try {
          const infoRes = await runtimeFetch(`${hubApi}/info`);
          const infoData = await infoRes.json();
          if (infoData?.systemName) hubName = infoData.systemName;
          hubImage = infoData?.imageUrl;
          hubIcon = infoData?.iconUrl;
          hubId = infoData?.hubId;
        } catch {
          // Keep defaults
        }
        // Remember the hub key for per-hub UI state (upgrade to hubId when
        // known so the key survives IP changes).
        try {
          localStorage.setItem('otto_active_hub_key', hubId ? `hub:${hubId}` : getHubSessionKey());
        } catch {
          // ignore
        }
        await saveConnection({
          id: connId,
          name: hubName,
          ip: resolvedIp,
          port: resolvedPort,
          // The scheme this session is on, so a reconnect dials the transport
          // that worked instead of assuming cleartext (card `t_cf6ea0e5`).
          secure: activeSecure,
          isCloud: false,
          hubId,
          image: hubImage,
          icon: hubIcon,
        });
        await saveConnectionCredentials(connId, email, password);
      }

      sessionExpiredNotified.value = false;

      return { success: true };
    } catch (__caught: unknown) {
      const error = __caught as {
        response?: {
          status?: number;
          data?: { error?: string; message?: string; details?: string; [k: string]: unknown };
        };
        message?: string;
      };
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || 'Hub login failed';
      return { success: false, error: msg };
    }
  }

  /**
   * Drop the hub-side identity (`user`) without touching the cloud session.
   *
   * Called when the client switches to a DIFFERENT hub: a hub user id exists on
   * exactly one hub, so the previous hub's id must not survive into the new
   * session. Surfaces keyed on it (top-bar avatar `GET /users/:id/image`, the
   * user drawer's `GET /users/:id/devices`, the account page) otherwise request
   * a user that does not exist there — 404, empty avatars and an empty devices
   * list, plus pages opened for that id render "User not found". The next
   * fetchHubUser() repopulates the identity from the new hub's `/users/me`.
   */
  function clearHubIdentity(): void {
    user.value = null;
    // A hub switch is a new claim decision: the NEXT hub may be unlinked even
    // though this one already answered (PLAN.md F13).
    hubClaimAttempted = false;
  }

  /**
   * Fetch the hub-side user profile (`/users/me`) through the routed API layer.
   *
   * Works in EVERY connection mode because `apiFetch` routes over the WebRTC
   * data channel when connected in cloud mode (the hub injects a local JWT for
   * the mapped cloud user) and through the Electron IPC bridge in Electron.
   *
   * In cloud mode the local `accessToken` is null, so `checkAuth()` early-returns
   * and `authStore.user` would otherwise stay null (the cause of the missing
   * avatar). Call this after the WebRTC connection is established to populate
   * the hub identity. It also auto-links the cloud account to the local hub user
   * by email when they are not already linked (see linkCloudUserIfNeeded).
   *
   * A hub that is linked to NO cloud account yet answers `identity_not_linked`
   * here; that refusal is recovered by `claimHubIdentity()` (once per session),
   * see PLAN.md F13.
   */
  async function fetchHubUser(): Promise<User | null> {
    const apiFetch = (await import('../services/api')).apiFetch;
    try {
      const data = await apiFetch('/users/me');
      if (!data?.id) {
        console.warn('[AuthStore] fetchHubUser: /users/me returned no user');
        return null;
      }
      user.value = normalizeUser(data);

      // Auto-link cloud account to the hub user by email when connected via cloud.
      if (cloudAccessToken.value && cloudUser.value) {
        await linkCloudUserIfNeeded();
      }

      return user.value;
    } catch (error) {
      // `identity_not_linked`: this hub is linked to NO cloud account yet, so the
      // tunnel refuses every hub call — including the `link-cloud` above, which
      // needs the local token the tunnel will not mint. That is a deadlock only
      // `POST /auth/claim` can break: it is the one tunneled path an unmapped
      // session may call, and it links the hub user whose email the CLOUD
      // verified. Spend it once, then read the identity again.
      if (
        shouldAttemptClaim({
          cloudAccessToken: cloudAccessToken.value,
          localAccessToken: accessToken.value,
          lastError: error,
          alreadyAttempted: hubClaimAttempted,
        })
      ) {
        // Latch BEFORE the attempt: a refusal must not be retried by the next
        // call of this function (the WebRTC store's 2s retry, or a reconnect).
        hubClaimAttempted = true;
        if (await claimHubIdentity()) {
          return user.value;
        }
        return null;
      }

      console.warn('[AuthStore] fetchHubUser failed:', error);
      return null;
    }
  }

  /**
   * Claim this hub for the signed-in cloud account — `notes/identity/PLAN.md` F13.
   *
   * Why: the WebRTC tunnel mints a local hub JWT only for a cloud user the hub
   * already maps in `users.cloud_user_id`. On a hub where nobody is linked yet
   * every hub call answers `401 identity_not_linked`, and
   * `POST /users/:id/link-cloud` cannot break that deadlock because it needs the
   * very token the tunnel refuses to mint. `POST /auth/claim` can: the hub
   * validates the client's cloud token against the cloud's `/api/oauth2/userinfo`,
   * matches the hub user by the **verified** cloud email, links it and answers
   * `{user, token}`.
   *
   * Callers must attempt this at most once per session (`shouldAttemptClaim`).
   * Returns true only when a usable hub token was adopted.
   */
  async function claimHubIdentity(): Promise<boolean> {
    const apiFetch = (await import('../services/api')).apiFetch;

    // Never claim over a live hub session, and never without a cloud identity:
    // there would be no token to validate and nothing to link.
    if (!cloudAccessToken.value || accessToken.value) return false;

    try {
      const data = await apiFetch('/auth/claim', {
        method: 'POST',
        // `authToken` puts the CLOUD bearer on the request instead of the (absent)
        // local hub token. The tunnel lets this one path through with the
        // client's own Authorization header *because* the route validates that
        // token server-side; passing it explicitly also keeps apiFetch's
        // 401-refresh path (which needs a local session) out of this call.
        authToken: cloudAccessToken.value,
        body: JSON.stringify({ cloudAccessToken: cloudAccessToken.value }),
      });

      const claimed = readClaimedIdentity<User>(data);
      if (!claimed) {
        // A success without a token would leave the app "connected" with no hub
        // session — adopt nothing rather than half of it.
        console.warn('[AuthStore] claimHubIdentity: hub returned no usable token');
        return false;
      }

      await adoptClaimedIdentity(claimed);

      console.log('[AuthStore] Hub claimed for the cloud account', {
        hubUserId: user.value?.id,
        role: user.value?.role,
      });
      return true;
    } catch (error) {
      // Never the token, never the whole error object: only the cause. The person
      // needs to know whether to sign in again or ask an administrator to invite
      // their address — a console line cannot tell them.
      const code = classifyClaimFailure(error);
      console.warn('[AuthStore] claimHubIdentity refused:', code);
      await notifyClaimFailure(code);
      return false;
    }
  }

  /**
   * Take a claimed `{token, user}` into this session — the ONE place that writes
   * a hub session, shared by the two doors that can produce one:
   *
   *   - `claimHubIdentity()` above, over the tunnel (a cloud session whose hub
   *     refused `identity_not_linked`);
   *   - `services/hubJoinFlow.ts`, which links over the hub's own LAN address
   *     when the person picks a hub on this network (card `t_dc2d87c6`).
   *
   * Both write exactly what `login()` writes (refs, storage, axios default
   * header, socket) and then re-read `/users/me` with the new token, because
   * everything keyed on the hub user id must see the claimed identity. A second
   * copy of these five lines is how one of them silently stops rotating.
   */
  async function adoptClaimedIdentity(claimed: ClaimedIdentity<User>): Promise<void> {
    accessToken.value = claimed.token;
    if (claimed.user) user.value = normalizeUser(claimed.user);
    persistLocalToken(claimed.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${claimed.token}`;
    sessionExpiredNotified.value = false;

    // One identity, not one identity plus a device credential (plan
    // `docs/plans/LOCAL-FIRST-CONNECT.md` §2/D4, card `t_6cbbce4c`): a claim
    // just proved who this account is to this hub, so the hub session above is
    // this device's door and a `typ:"device"` credential stored for the same
    // connection is the retired shape — dropped here, once, rather than left to
    // be offered to a route that wants a session. A panel or an installer's
    // device has no account, cannot claim, and never reaches this path.
    if (deviceCredential.value) {
      clearDeviceCredential();
      console.info('[AuthStore] claim landed: the stored device credential for this connection is retired');
    }
    // …and the host-scoped spelling a build before the port-scoped keys left
    // behind (`torik_device_token:local:<host>/api`) — the shape that made the
    // answer wrong for a hub this account owned. No per-connection clear reaches
    // it, and no current write creates it (`services/deviceCredential.ts`).
    const retiredLegacyKeys = clearHostScopedDeviceCredentials();
    if (retiredLegacyKeys.length) {
      console.info('[AuthStore] claim landed: host-scoped legacy credentials retired', retiredLegacyKeys);
    }

    const socketStore = useSocketStore();
    socketStore.connect(claimed.token);

    // Re-read through the tunnel with the new token: the hub now injects this
    // mapping, so `/users/me` is the authoritative profile for the session and
    // anything keyed on the hub user id sees the claimed identity.
    await fetchHubUserInternal();
  }

  /**
   * Surface a claim refusal in the user's language. The copy lives in
   * `src/locales/{en,sl}.ts` (`auth.claim.*`) — never hard-coded here.
   */
  async function notifyClaimFailure(code: ClaimFailureCode): Promise<void> {
    let message: string | null = null;
    try {
      const { getI18n } = await import('../locales');
      const gt = getI18n().global.t.bind(getI18n().global);
      message = gt(claimFailureMessageKey(code), { email: cloudUser.value?.email || '' });
    } catch (error) {
      console.warn('[AuthStore] i18n unavailable for the claim failure message:', error);
    }
    if (!message) return;

    try {
      const { useToastStore } = await import('./toast');
      useToastStore().addToast({ type: 'error', message, duration: 8000 });
    } catch {
      console.warn('[AuthStore] Failed to show the claim failure toast');
    }
  }

  /**
   * Link the cloud account (cloudUser) to the hub user (user) by email.
   *
   * The hub maps cloud sessions to local users via `users.cloud_user_id`, and
   * falls back to the first enabled admin when no mapping exists. To give the
   * correct identity/avatar/permissions, ask the hub to link the cloud account
   * to the local user whose email matches the cloud account email.
   *
   * The hub validates the cloud access token itself (via /api/oauth2/userinfo),
   * so the email is never trusted from the client.
   */
  async function linkCloudUserIfNeeded(): Promise<boolean> {
    const apiFetch = (await import('../services/api')).apiFetch;
    const hubUser = user.value;
    const cloudAccount = cloudUser.value;
    if (!hubUser?.id || !cloudAccessToken.value || !cloudAccount) return false;

    // Already linked to this exact cloud account?
    if (hubUser.cloudUserId && hubUser.cloudUserId === cloudAccount.id) return false;

    // Otherwise attempt the link. The hub is the authority: it validates the
    // cloud access token via /api/oauth2/userinfo and matches by email. This
    // also fixes the admin-fallback impersonation — an unlinked cloud user who
    // was mapped to the first admin gets re-mapped to their own local account
    // (by email) on first connect.
    try {
      const result = await apiFetch(`/users/${encodeURIComponent(hubUser.id)}/link-cloud`, {
        method: 'POST',
        body: JSON.stringify({ cloudAccessToken: cloudAccessToken.value }),
      });
      console.log('[AuthStore] Cloud account linked to hub user:', {
        hubUserId: hubUser.id,
        cloudUserId: cloudAccount.id,
        result,
      });
      // Refresh the hub user so cloudUserId is reflected.
      await fetchHubUserInternal();
      return true;
    } catch (error) {
      // Non-fatal: fall back to the hub's current mapping (possibly admin).
      console.warn('[AuthStore] link-cloud failed (non-fatal):', error);
      return false;
    }
  }

  /** Internal /users/me fetch that skips the link step (avoids recursion). */
  async function fetchHubUserInternal(): Promise<User | null> {
    const apiFetch = (await import('../services/api')).apiFetch;
    try {
      const data = await apiFetch('/users/me');
      if (data?.id) {
        user.value = normalizeUser(data);
        return user.value;
      }
    } catch (error) {
      console.warn('[AuthStore] fetchHubUserInternal failed:', error);
    }
    return null;
  }

  /**
   * Tear down every active connection: WebRTC peer + signaling socket and the
   * hub socket.io connection. Keeps the cloud account signed in (per product
   * decision, logout closes the hub connection only).
   */
  function disconnectAll(): void {
    try {
      const webrtcStore = useWebRTCStore();
      if (webrtcStore.connected || webrtcStore.connecting) {
        webrtcStore.disconnect();
      }
    } catch (e) {
      console.warn('[AuthStore] WebRTC disconnect on logout failed (safe to ignore):', e);
    }
    try {
      const socketStore = useSocketStore();
      socketStore.disconnect();
    } catch (e) {
      console.warn('[AuthStore] Socket disconnect on logout failed (safe to ignore):', e);
    }
  }

  async function checkAuth() {
    if (!accessToken.value) {
      return false;
    }

    try {
      const runtimeConfig = getRuntimeConfig();
      if (isCapacitor() && (!runtimeConfig.apiUrl || runtimeConfig.apiUrl.startsWith('/'))) {
        console.warn('[AuthStore] No active hub API URL in Capacitor mode; clearing stale auth tokens');
        logout();
        return false;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken.value}`;

      // Verify token and get user info
      // In Capacitor mode the runtime keeps mode='capacitor' (not 'local') even after
      // restoring the saved hub IP, so we must explicitly check isCapacitor() here too.
      const apiUrl = getApiUrl();
      const meEndpoint = isLocal() || isCapacitor() ? '/users/me' : '/auth/me';
      const data = await runtimeJsonRequest<User>(`${apiUrl}${meEndpoint}`, {
        headers: {
          Authorization: `Bearer ${accessToken.value}`,
        },
      });
      user.value = normalizeUser(data);

      // This hub just accepted the token this window holds, so the token IS this
      // connection's: record it under this window's key (migration for a token
      // that only ever lived under the unscoped key, and re-attribution for one
      // held across a hub switch). Until this ran, a token inherited from
      // another hub was presented anyway, and the 401 that followed was read as
      // an expired session (card `t_68d110ee`).
      if (accessToken.value) persistLocalToken(accessToken.value);

      // Reconnect socket only after the token is verified — and never from the
      // desktop launcher home (card t_f0ea3681): the picker keeps the stored
      // session (that is what was just verified) but opens no hub connection, so
      // this boot path is where the defect started and where it must not.
      // `stores/socket.ts` refuses it anyway; the check is repeated here so the
      // log states what happened instead of claiming a reconnect that did not.
      const socketStore = useSocketStore();
      if (!onDesktopLauncherHome() && !socketStore.connected) {
        socketStore.connect(accessToken.value);
        console.log('[AuthStore] Socket reconnected on checkAuth');
      }

      sessionExpiredNotified.value = false;

      return true;
    } catch (error) {
      console.error('[AuthStore] Auth check failed:', error);
      logout();
      return false;
    }
  }

  // ── First-user setup / public hub state ──────────────────────────────────
  const setupStatus = ref<{
    needsSetup: boolean;
    hasUsers: boolean;
    systemName?: string;
    systemImageUrl?: string | null;
    cloudRegistered?: boolean;
  } | null>(null);

  /**
   * Fetch the hub's public setup status (no auth required). Used by the
   * login page for branding and by the router guard to redirect to the
   * first-user setup wizard when the hub has no users yet.
   */
  async function fetchSetupStatus(force = false) {
    if (!force && setupStatus.value) return setupStatus.value;
    // `/setup/status` is a HUB route — only a hub answers it. On a cloud
    // session the API base is the cloud origin, where the path is not a route:
    // the router guard calls this on every navigation, so the request failed
    // (and logged a warning) for every route change. The first-user wizard is
    // a hub-local concept — report "unknown" instead of probing the cloud.
    const apiUrl = getApiUrl();
    if (isCloudApiBase(apiUrl)) return null;
    try {
      const data = await runtimeJsonRequest(`${apiUrl}/setup/status`);
      setupStatus.value = {
        needsSetup: !!data.needsSetup,
        hasUsers: !!data.hasUsers,
        systemName: data.systemName,
        systemImageUrl: data.systemImageUrl,
        cloudRegistered: !!data.cloudRegistered,
      };
      return setupStatus.value;
    } catch (error) {
      console.warn('[AuthStore] Failed to fetch setup status:', error);
      return null;
    }
  }

  // The hub serves no password-reset door any more (PLAN.md §4 phase 4, hub
  // commit 51677c7): a person gets in with their Torik account, with an invite
  // from the owner, or with this hub's own local password (D1), and
  // `POST /api/auth/login` survives only for
  // `legacy` accounts during the migration. The app therefore offers no
  // password-recovery link at all — a link to one would answer 404.

  // ── Device credential (PLAN.md §3, F5) ────────────────────────────────
  //
  // A hub that admits a device itself — a paired panel, an adopted hub — hands it
  // a credential that hub signed
  // (`{sub, role, dev, typ:"device"}`, 90 days, rotated on use). It is NOT a
  // user session and deliberately does not set `isAuthenticated`: the hub
  // answers `/users/me` with 401 for a device credential, because the device
  // acts for its bound USER on the routes the hub mounts with `authenticateAny`
  // — `/devices`, `/rooms`, `/scenes`, `/energy`, … — which is the whole of
  // what `services/api.ts` offers it for.
  //
  // It gets its own session concept here, beside the user and cloud ones, so
  // there is ONE owner of the credential: this store keeps the reactive copy,
  // the storage write, the rotation call and the user-visible consequence of a
  // refusal. `services/deviceCredential.ts` owns the policy and the storage
  // shape; the API layer only asks this store what to put in the header.

  /** The credential is per connection — a hub's signature means nothing elsewhere. */
  const deviceCredential = ref<StoredDeviceCredential | null>(readDeviceCredential());
  const deviceCredentialToken = computed(() => deviceCredential.value?.token ?? null);
  const isDeviceAuthenticated = computed(() => !!deviceCredential.value);
  /** What the credential says this device is, for the screens that show it. */
  const deviceSession = computed(() => {
    const credential = deviceCredential.value;
    if (!credential) return null;
    return {
      deviceKey: credential.claims.dev,
      userId: credential.claims.sub,
      role: credential.claims.role,
      expiresAt: credential.expiresAtMs ? new Date(credential.expiresAtMs).toISOString() : null,
    };
  });
  /** A refusal is announced once, however many parallel 401s report it. */
  const deviceCredentialRejected = ref(false);
  let deviceRefreshInFlight: Promise<DeviceRefreshOutcome> | null = null;
  /** The verdict question itself: one probe at a time, whatever asks. */
  let deviceProbeInFlight: Promise<DeviceProbeVerdict> | null = null;

  // A credential stored before credentials were hub-scoped sits under the shared
  // key. Adopting it once moves it under this connection's keys, so it can no
  // longer be offered to whatever hub this app points at next.
  if (deviceCredential.value && deviceCredential.value.storageKey === DEVICE_CREDENTIAL_STORAGE_KEY) {
    const migrated = storeDeviceCredential(deviceCredential.value.token);
    if (migrated) deviceCredential.value = migrated;
  }

  /** Take ownership of the credential the hub just issued (redeem) or rotated. */
  function adoptDeviceCredential(newToken: string): StoredDeviceCredential | null {
    const stored = storeDeviceCredential(newToken);
    if (!stored) {
      console.warn('[AuthStore] adoptDeviceCredential: not a device credential');
      return null;
    }
    deviceCredential.value = stored;
    deviceCredentialRejected.value = false;
    return stored;
  }

  /**
   * This session's credential as of NOW.
   *
   * The reactive copy is the normal source; the storage read is what makes the
   * other writers work — a redeem this session did not perform (another module,
   * another tab), and a credential stored under the legacy shared key by an
   * older build. Nothing is read when this session already holds one, so a
   * credential REJECTED here cannot come back from storage.
   */
  function resolveDeviceCredential(): StoredDeviceCredential | null {
    if (deviceCredential.value) return deviceCredential.value;
    const stored = readDeviceCredential();
    if (stored) {
      deviceCredential.value =
        stored.storageKey === DEVICE_CREDENTIAL_STORAGE_KEY
          ? // Migrate the pre-scoping credential to this connection's keys.
            (storeDeviceCredential(stored.token) ?? stored)
          : stored;
    }
    return deviceCredential.value;
  }

  /** Forget this device's credential (unpair / a fresh enrollment). */
  function clearDeviceCredential(): void {
    clearStoredDeviceCredential();
    deviceCredential.value = null;
  }

  /**
   * Is the credential close enough to expiry that it should rotate before the
   * next request? The credential lives 90 days and the hub rotates it on use, so
   * a device opened at least once a fortnight never meets an expiry it cannot
   * come back from.
   */
  function deviceCredentialNeedsRefresh(now: number = Date.now()): boolean {
    return shouldRefreshDeviceCredential(resolveDeviceCredential(), now);
  }

  /**
   * `POST /auth/device/refresh` (F5): the hub rotates the credential on use and
   * the rotated one replaces the stored one — a RENEWAL, and the route this app
   * has always used.
   *
   * Precisely because it rotates, it is not the route to ask a question with:
   * the credential presented here is refused from the moment this answers (card
   * `t_6b11b40d`). The API layer's 401 verdict asks `probeDeviceCredential()`
   * instead, so this call is reached in two places only: ahead of a request
   * whose credential is inside the refresh skew — that call *wants* a successor
   * — and as the probe's fallback on a hub that has no probe route.
   */
  async function refreshDeviceCredential(): Promise<DeviceRefreshOutcome> {
    const current = resolveDeviceCredential();
    if (!current) return 'none';
    // One rotation at a time: the hub rotates on use, so parallel calls would
    // leave all but the last rotated credential unusable.
    if (deviceRefreshInFlight) return deviceRefreshInFlight;

    deviceRefreshInFlight = (async (): Promise<DeviceRefreshOutcome> => {
      try {
        const apiFetch = (await import('../services/api')).apiFetch;
        const result = await apiFetch<{ deviceToken?: string }>('/auth/device/refresh', {
          method: 'POST',
          // An explicit token: the API layer manages the credential only when it
          // selected it itself, so this call cannot recurse into its own policy.
          authToken: current.token,
          skipAuthRefresh: true,
        });
        if (result?.deviceToken) {
          adoptDeviceCredential(result.deviceToken);
          console.log('[AuthStore] Device credential rotated');
        }
        return 'rotated';
      } catch (error) {
        if (isDeviceCredentialRejected(error)) {
          console.warn('[AuthStore] The hub refused this device credential — dropping it');
          clearDeviceCredential();
          await notifyDeviceCredentialRejected();
          return 'invalid';
        }
        // A network blip or a 500 is not a verdict about the credential.
        return 'unavailable';
      } finally {
        deviceRefreshInFlight = null;
      }
    })();

    return deviceRefreshInFlight;
  }

  /**
   * The verdict on this connection's device credential — ASKED, never spent
   * (card `t_7aa9066f`).
   *
   * The API layer calls this when a request that carried the credential was
   * answered 401: only the credential's own route can say whether the credential
   * is dead, and asking it with the renewal spends the thing it is asking about.
   * `services/deviceCredentialProbe.ts` owns the sequence — `GET` first, the
   * renewal only where this hub has no probe route (404/405) — and this action
   * is the session half of it: which credential to ask about, what a renewal is
   * here, and what a refusal does (drop, then say so once on the sign-in screen —
   * there is no code surface to send anyone to any more, card `t_2f0c4a91` — and
   * the refused credential is never presented again).
   *
   * A `200` on the probe means the credential is good and the 401 that raised
   * the question was about something else, so the credential is left exactly as
   * it was: nothing is rotated, nothing is dropped, and the next request
   * presents the same credential.
   */
  async function probeDeviceCredential(): Promise<DeviceProbeVerdict> {
    // One question at a time: a page of parallel reads that all 401 ask the same
    // thing, and the hub has to answer it once. (The renewal inside is
    // single-flight for its own reason — it writes.)
    if (deviceProbeInFlight) return deviceProbeInFlight;

    deviceProbeInFlight = (async (): Promise<DeviceProbeVerdict> => {
      try {
        return await runDeviceCredentialProbe({
          credential: resolveDeviceCredential()?.token ?? null,
          probe: async (token) => {
            const apiFetch = (await import('../services/api')).apiFetch;
            await apiFetch(DEVICE_CREDENTIAL_PROBE_ENDPOINT, {
              method: 'GET',
              // An explicit token, as the renewal does: the API layer manages the
              // credential only when it selected it itself, so this call cannot
              // recurse into its own policy.
              authToken: token,
              skipAuthRefresh: true,
            });
          },
          renew: () => refreshDeviceCredential(),
          refuse: async () => {
            clearDeviceCredential();
            await notifyDeviceCredentialRejected();
          },
        });
      } finally {
        deviceProbeInFlight = null;
      }
    })();

    return deviceProbeInFlight;
  }

  /**
   * The hub will not accept this device's credential any more (revoked, or its
   * binding expired): drop it and send the person to **their own** way in.
   *
   * Which way that is is a decision, not a constant (card `t_bfe0edc9`, measured:
   * a signed-in owner with a hub on this network was painted the *guest code*
   * screen): an owner — a Torik account signed in on this device, the hub at an
   * address on this device's own network — is sent to the **Local page**, named
   * and explained, because a box the owner runs needs no code at all ("the
   * adoption should happen on the Local page"). A device with no account is sent
   * to the **sign-in screen** (`/login`), whose ways in are the account and this
   * hub's own local password — there is no code to scan any more, for anyone
   * (card `t_2f0c4a91`). `utils/hubOpenFallback.ts` is the whole table; this
   * function only asks it.
   *
   * Announced once per refusal, and it never retries the refused credential —
   * retrying is what would make this a loop.
   */
  async function notifyDeviceCredentialRejected(): Promise<void> {
    if (deviceCredentialRejected.value) return;
    deviceCredentialRejected.value = true;

    const endpoint = getActiveHubEndpoint();
    const plan = resolveHubOpenPlan({
      failure: 'credential-refused',
      accountSignedIn: isCloudAuthenticated.value,
      hubName: endpoint?.name ?? null,
      address: endpoint ? { ip: endpoint.ip, port: endpoint.port } : null,
    });

    let message: string | null = null;
    try {
      const { getI18n } = await import('../locales');
      const gt = getI18n().global.t.bind(getI18n().global);
      // An owner gets the plan's own sentence (it says where the way in is and
      // that no code is needed); a device with no account gets the same story in
      // the words of the sign-in screen it is being sent to.
      const key = plan.surface === 'local' ? plan.messageKey : 'auth.credentialRejected';
      message = gt(key, {
        hub: endpoint?.name || gt('enroll.hub.unknown'),
        address: endpoint ? `${endpoint.ip}:${endpoint.port}` : '',
      });
    } catch (error) {
      console.warn('[AuthStore] i18n unavailable for the device-credential message:', error);
    }
    if (message) {
      try {
        const { useToastStore } = await import('./toast');
        useToastStore().addToast({ type: 'warning', message, duration: 8000 });
      } catch {
        console.warn('[AuthStore] Failed to show the device-credential toast');
      }
    }

    try {
      const router = (await import('../router')).default;
      if (router.currentRoute.value.path !== plan.route.path) {
        await router.push(plan.route);
      }
    } catch (error) {
      console.warn('[AuthStore] Device-credential redirect failed:', error);
    }
  }

  return {
    accessToken,
    refreshToken,
    token, // Backward compatibility alias for accessToken
    user,
    isAuthenticated,
    isAdmin,
    setupStatus,
    fetchSetupStatus,
    // Enrolled device credential (F5)
    deviceCredential,
    deviceCredentialToken,
    isDeviceAuthenticated,
    deviceSession,
    adoptDeviceCredential,
    resolveDeviceCredential,
    clearDeviceCredential,
    deviceCredentialNeedsRefresh,
    refreshDeviceCredential,
    probeDeviceCredential,
    notifyDeviceCredentialRejected,
    // Which local hub JWT may be presented to the connection in use (t_68d110ee)
    connectionUserToken,
    localTokenIsAttributable,
    dropUnattributableLocalToken,
    // Cloud auth
    cloudAccessToken,
    cloudUser,
    isCloudAuthenticated,
    loginToCloudViaOAuth,
    refreshCloudAccessToken,
    ensureCloudToken,
    fetchCloudUser,
    logoutFromCloud,
    invalidateCloudSession,
    initFromWebSession,
    // Hub auth
    loginToHub,
    login,
    loginWithOAuth,
    handleOAuthCallback,
    logout,
    checkAuth,
    fetchHubUser,
    clearHubIdentity,
    linkCloudUserIfNeeded,
    claimHubIdentity,
    /**
     * Adopt a claimed hub session (`{token, user}`) into this store. The LAN
     * join (`services/hubJoinFlow.ts`) links over the hub's own address and
     * hands the answer here, so both doors write the session the same way.
     */
    adoptClaimedIdentity,
    disconnectAll,
    refreshAccessToken,
    refreshLocalHubToken,
    isLocalTokenExpiringSoon,
    notifySessionExpired,
  };
});
